<script>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { validateTextInputSafety } from "@/utils/contentSafetyFilter";
import { logWarn } from "@/utils/secureLogger";
import { sanitizeHtml } from "@/utils/xssSanitizer";
import Swal from "sweetalert2";
import { eventBus, EVENTS } from "@/utils/eventBus";

export default {
  name: "FileReport",
  setup() {
    // Modal state
    const isModalOpen = ref(false);
    const isSubmitting = ref(false);
    const submitMessage = ref("");
    const submitType = ref(""); // 'success' or 'error'

    // Success toast state
    const showSuccessToast = ref(false);
    const successToastMessage = ref("");
    const successReportId = ref("");

    // Draggable button state
    const buttonPosition = ref({ x: 0, y: 0 }); // Offset from default position
    const isDragging = ref(false);
    const hasDragged = ref(false); // Track if user actually moved the button
    const dragStart = ref({ x: 0, y: 0 });
    const buttonRef = ref(null);
    const lastClickTime = ref(0);

    // User authentication state
    const currentUser = ref(null);
    const isLoggedIn = ref(false);

    // Picture upload state
    const uploadedPictures = ref([]);
    const pictureInput = ref(null);
    const MAX_PICTURES = 3;
    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes
    const ALLOWED_TYPES = ["image/jpeg", "image/png"];

    // Form data
    // Try to instantly set user info if available
    let initialName = "Guest";
    let initialEmail = "";
    let initialType = "guest";
    if (window?.localStorage) {
      try {
        const userData = JSON.parse(localStorage.getItem("userData") || "null");
        if (userData && (userData.userType === "student" || userData.userType === "tutor")) {
          initialName =
            [userData.firstName, userData.middleName, userData.lastName, userData.suffix]
              .filter(Boolean)
              .join(" ") || "Guest";
          initialEmail = userData.email || "";
          initialType = userData.userType;
        }
      } catch {}
    }
    const formData = ref({
      reporterName: initialName,
      reporterEmail: initialEmail,
      reporterType: initialType,
      reportedName: "",
      reportedEmail: "",
      reportedType: "", // system, student, tutor (required)
      category: "",
      problemDetail: "",
    });

    // Track field interactions to show validation only after user touches fields
    const fieldTouched = ref({
      reporterEmail: false,
      reportedName: false,
      reportedEmail: false,
      reportedType: false,
      category: false,
      problemDetail: false,
    });

    // Character count for problem detail
    const problemDetailCharCount = computed(() => formData.value.problemDetail.length);
    const problemDetailMaxLength = 300;
    const problemDetailMinLength = 20;

    // Character limits for reported name
    const reportedNameMaxLength = 50;
    const reportedNameMinLength = 5;

    // Validation functions
    const isValidName = (name) => {
      if (!name.trim()) return false;
      if (name.length < reportedNameMinLength || name.length > reportedNameMaxLength) return false;
      // Allow letters, spaces, apostrophes, hyphens, and dots (for names like "O'Connor", "Mary-Jane", "Dr.")
      const nameRegex = /^[a-zA-Z\s\'\-\.]+$/;
      return nameRegex.test(name.trim());
    };

    const isValidEmail = (email) => {
      if (!email.trim()) return false; // Email is required
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email.trim());
    };

    // Computed validations
    const reportedNameError = computed(() => {
      if (!fieldTouched.value.reportedName) return "";
      const name = formData.value.reportedName.trim();
      if (!name) return "Name/Subject is required";
      if (name.length < reportedNameMinLength)
        return `Minimum ${reportedNameMinLength} characters required`;
      if (name.length > reportedNameMaxLength)
        return `Maximum ${reportedNameMaxLength} characters allowed`;
      if (!isValidName(name))
        return "Only letters, spaces, apostrophes, hyphens, and dots are allowed";
      return "";
    });

    const reporterEmailError = computed(() => {
      if (!fieldTouched.value.reporterEmail) return "";
      const email = formData.value.reporterEmail.trim();
      if (!email) return "Email is required";
      if (!isValidEmail(email)) return "Please enter a valid email address";
      return "";
    });

    const reportedEmailError = computed(() => {
      if (!fieldTouched.value.reportedEmail) return "";
      const email = formData.value.reportedEmail.trim();
      if (email && !isValidEmail(email)) return "Please enter a valid email address";
      return "";
    });

    // Enhanced form validation with character limits and required fields
    const isFormValid = computed(() => {
      return (
        formData.value.reporterName.trim() &&
        formData.value.reporterEmail.trim() && // Reporter email is required
        isValidEmail(formData.value.reporterEmail) && // Reporter email must be valid
        isValidName(formData.value.reportedName) && // Name validation
        formData.value.reportedType &&
        formData.value.category &&
        formData.value.problemDetail.trim() &&
        formData.value.problemDetail.length >= problemDetailMinLength &&
        formData.value.problemDetail.length <= problemDetailMaxLength &&
        (!formData.value.reportedEmail.trim() || isValidEmail(formData.value.reportedEmail)) &&
        !reportedNameError.value &&
        !reporterEmailError.value &&
        !reportedEmailError.value
      );
    });

    // Available categories
    const categories = [
      "Inappropriate Behavior",
      "Technical Issue",
      "No Show",
      "Poor Quality",
      "Other",
    ];

    // User types
    const userTypes = [
      { value: "guest", label: "Guest" },
      { value: "student", label: "Student" },
      { value: "tutor", label: "Tutor" },
    ];

    const reportedUserTypes = [
      { value: "system", label: "System/Platform" },
      { value: "student", label: "Student" },
      { value: "tutor", label: "Tutor" },
    ];

    // Check user authentication and load user data
    const checkUserAuth = async () => {
      try {
        // Import Firebase functions
        const { auth, db, doc, getDoc } = await import("@/firebase");

        // Check if user is authenticated
        const user = auth.currentUser;
        if (user) {
          console.log("User is logged in:", user.uid);
          isLoggedIn.value = true;
          currentUser.value = user;

          // Only fetch user data if logged in
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Auto-fill user information for logged-in users
            const fullName = [
              userData.firstName,
              userData.middleName,
              userData.lastName,
              userData.suffix,
            ]
              .filter(Boolean)
              .join(" ");

            formData.value.reporterName = fullName || "Guest";
            formData.value.reporterEmail = userData.email || "";
            formData.value.reporterType = userData.userType || "guest";

            console.log("User data loaded:", {
              name: fullName,
              email: userData.email,
              type: userData.userType,
            });
          } else {
            console.log("User document not found, using default values");
            formData.value.reporterName = "Guest";
            formData.value.reporterEmail = "";
            formData.value.reporterType = "guest";
          }
        } else {
          console.log("User is NOT logged in - setting guest values");
          // Not logged in - set default guest values and strictly enforce
          isLoggedIn.value = false;
          currentUser.value = null;
          formData.value.reporterName = "Guest";
          formData.value.reporterEmail = ""; // Empty email for guests
          formData.value.reporterType = "guest";
        }
      } catch (error) {
        console.error("Error checking user authentication:", error);
        // Default to guest values on error - strict enforcement
        console.log("Authentication error - defaulting to guest");
        isLoggedIn.value = false;
        currentUser.value = null;
        formData.value.reporterName = "Guest";
        formData.value.reporterEmail = ""; // Empty email for guests
        formData.value.reporterType = "guest";
      }
    };

    // Modal controls
    const openModal = async () => {
      console.log("🚀 FileReport: openModal() called - Opening modal...");
      isModalOpen.value = true;
      console.log("✅ FileReport: isModalOpen set to true");
      await checkUserAuth();
      // checkUserAuth already sets formData values, no need to override them here
      resetForm();
      console.log("✅ FileReport: Modal opened successfully");
    };

    const closeModal = () => {
      isModalOpen.value = false;
      submitMessage.value = "";
      submitType.value = "";
    };

    const closeSuccessToast = () => {
      showSuccessToast.value = false;
    };

    const resetForm = () => {
      // Keep user info if logged in
      const keepName = formData.value.reporterName;
      const keepEmail = formData.value.reporterEmail;
      const keepType = formData.value.reporterType;

      formData.value = {
        reporterName: keepName,
        reporterEmail: keepEmail,
        reporterType: keepType,
        reportedName: "",
        reportedEmail: "",
        reportedType: "",
        category: "",
        problemDetail: "",
      };

      // Reset field touched state
      fieldTouched.value = {
        reporterEmail: false,
        reportedName: false,
        reportedEmail: false,
        reportedType: false,
        category: false,
        problemDetail: false,
      };

      submitMessage.value = "";
      submitType.value = "";
      uploadedPictures.value = [];
    };

    // Picture upload handlers
    const triggerPictureUpload = () => {
      pictureInput.value?.click();
    };

    const handlePictureUpload = (event) => {
      const files = Array.from(event.target.files || []);

      // Calculate how many slots are available
      const availableSlots = MAX_PICTURES - uploadedPictures.value.length;

      // Check if user is trying to upload when limit is reached
      if (availableSlots === 0) {
        Swal.fire({
          icon: "warning",
          title: "Maximum Pictures Reached",
          text: `You can only upload up to ${MAX_PICTURES} pictures. Please remove some pictures before uploading more.`,
          confirmButtonColor: "#3b82f6",
        });
        event.target.value = "";
        return;
      }

      // Check if user selected more files than available slots
      if (files.length > availableSlots) {
        Swal.fire({
          icon: "warning",
          title: "Too Many Pictures Selected",
          text: `You selected ${files.length} pictures, but only ${availableSlots} slot${
            availableSlots !== 1 ? "s" : ""
          } available. Only the first ${availableSlots} picture${
            availableSlots !== 1 ? "s" : ""
          } will be uploaded.`,
          confirmButtonColor: "#3b82f6",
        });
      }

      // Track validation errors
      const errors = [];
      let processedCount = 0;

      // Process only the files that fit within the limit
      const filesToProcess = files.slice(0, availableSlots);

      filesToProcess.forEach((file) => {
        // Check for duplicate file names
        const isDuplicate = uploadedPictures.value.some(
          (pic) => pic.name === file.name && pic.file.size === file.size
        );

        if (isDuplicate) {
          errors.push(`"${file.name}" is already uploaded.`);
          return;
        }

        // Validate file type
        if (!ALLOWED_TYPES.includes(file.type)) {
          errors.push(`"${file.name}" is not a supported format. Only JPEG and PNG are allowed.`);
          return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          errors.push(`"${file.name}" is too large (max 2MB).`);
          return;
        }

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          uploadedPictures.value.push({
            file: file,
            preview: e.target.result,
            name: file.name,
          });
          processedCount++;

          // Show success message only after all valid files are processed
          if (
            processedCount ===
              filesToProcess.filter((f) => {
                const notDuplicate = !uploadedPictures.value
                  .slice(0, -processedCount)
                  .some((pic) => pic.name === f.name && pic.file.size === f.size);
                return ALLOWED_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE && notDuplicate;
              }).length &&
            errors.length === 0 &&
            files.length <= availableSlots
          ) {
            // Only show success for single file uploads or when all files are valid
            if (processedCount === 1) {
              Swal.fire({
                icon: "success",
                title: "Picture Added",
                text: `Successfully added "${file.name}".`,
                timer: 2000,
                showConfirmButton: false,
              });
            }
          }
        };
        reader.readAsDataURL(file);
      });

      // Show errors if any
      if (errors.length > 0) {
        Swal.fire({
          icon: "error",
          title: "Upload Issues",
          html: `<div style="text-align: left;">${errors
            .map((err) => `• ${err}`)
            .join("<br>")}</div>`,
          confirmButtonColor: "#ef4444",
        });
      }

      // Reset input
      event.target.value = "";
    };

    const removePicture = (index) => {
      uploadedPictures.value.splice(index, 1);
    };

    // Mark field as touched when user interacts with it
    const markFieldAsTouched = (fieldName) => {
      fieldTouched.value[fieldName] = true;
    };

    // Field refs for scrolling
    const reporterEmailRef = ref(null);
    const reportedNameRef = ref(null);
    const reportedTypeRef = ref(null);
    const categoryRef = ref(null);
    const problemDetailRef = ref(null);

    // Validate and scroll to first missing field
    const validateAndScrollToError = () => {
      // Mark all fields as touched
      fieldTouched.value.reporterEmail = true;
      fieldTouched.value.reportedName = true;
      fieldTouched.value.reportedType = true;
      fieldTouched.value.category = true;
      fieldTouched.value.problemDetail = true;

      // Check required fields in order and scroll to first error
      if (!formData.value.reporterEmail.trim() || !isValidEmail(formData.value.reporterEmail)) {
        reporterEmailRef.value?.scrollIntoView({ behavior: "smooth", block: "center" });
        reporterEmailRef.value?.focus();
        return false;
      }

      if (!formData.value.reportedName.trim() || reportedNameError.value) {
        reportedNameRef.value?.scrollIntoView({ behavior: "smooth", block: "center" });
        reportedNameRef.value?.focus();
        return false;
      }

      if (!formData.value.reportedType) {
        reportedTypeRef.value?.scrollIntoView({ behavior: "smooth", block: "center" });
        reportedTypeRef.value?.focus();
        return false;
      }

      if (!formData.value.category) {
        categoryRef.value?.scrollIntoView({ behavior: "smooth", block: "center" });
        categoryRef.value?.focus();
        return false;
      }

      if (
        !formData.value.problemDetail.trim() ||
        formData.value.problemDetail.length < problemDetailMinLength ||
        formData.value.problemDetail.length > problemDetailMaxLength
      ) {
        problemDetailRef.value?.scrollIntoView({ behavior: "smooth", block: "center" });
        problemDetailRef.value?.focus();
        return false;
      }

      return true;
    };

    // Submit report to Firebase
    const submitReport = async () => {
      if (isSubmitting.value) return;

      // Validate all fields and scroll to first error
      if (!validateAndScrollToError()) {
        return;
      }

      // Validate content safety for reporter name
      const reporterNameCheck = validateTextInputSafety(formData.value.reporterName);
      if (!reporterNameCheck.isSafe) {
        Swal.fire({
          icon: "error",
          title: "Inappropriate Content Detected",
          html: `
            <p class="mb-3">The unsafe content has been automatically removed.</p>
            <p class="mb-3">ElevatED is an educational platform designed for high school students. All content must be:</p>
            <ul class="text-left list-disc pl-6 mb-3">
              <li>Educational and age-appropriate</li>
              <li>Free from profanity, explicit language, and violence</li>
              <li>Suitable for a learning environment</li>
            </ul>
          `,
          confirmButtonColor: "#ef4444",
          confirmButtonText: "I Understand",
          allowOutsideClick: false,
          allowEscapeKey: false,
          width: "600px",
          customClass: {
            container: "swal-on-top",
          },
        });
        logWarn("Inappropriate content detected in reporter name", {
          detectedTerms: reporterNameCheck.detectedTerms,
        });
        formData.value.reporterName = "";
        return;
      }

      // Validate content safety for reported name
      const reportedNameCheck = validateTextInputSafety(formData.value.reportedName);
      if (!reportedNameCheck.isSafe) {
        Swal.fire({
          icon: "error",
          title: "Inappropriate Content Detected",
          html: `
            <p class="mb-3">The unsafe content has been automatically removed.</p>
            <p class="mb-3">ElevatED is an educational platform designed for high school students. All content must be:</p>
            <ul class="text-left list-disc pl-6 mb-3">
              <li>Educational and age-appropriate</li>
              <li>Free from profanity, explicit language, and violence</li>
              <li>Suitable for a learning environment</li>
            </ul>
          `,
          confirmButtonColor: "#ef4444",
          confirmButtonText: "I Understand",
          allowOutsideClick: false,
          allowEscapeKey: false,
          width: "600px",
          customClass: {
            container: "swal-on-top",
          },
        });
        logWarn("Inappropriate content detected in reported name", {
          detectedTerms: reportedNameCheck.detectedTerms,
        });
        formData.value.reportedName = "";
        return;
      }

      // Validate content safety for problem detail
      const problemDetailCheck = validateTextInputSafety(formData.value.problemDetail);
      if (!problemDetailCheck.isSafe) {
        Swal.fire({
          icon: "error",
          title: "Inappropriate Content Detected",
          html: `
            <p class="mb-3">The unsafe content has been automatically removed.</p>
            <p class="mb-3">ElevatED is an educational platform designed for high school students. All content must be:</p>
            <ul class="text-left list-disc pl-6 mb-3">
              <li>Educational and age-appropriate</li>
              <li>Free from profanity, explicit language, and violence</li>
              <li>Suitable for a learning environment</li>
            </ul>
          `,
          confirmButtonColor: "#ef4444",
          confirmButtonText: "I Understand",
          allowOutsideClick: false,
          allowEscapeKey: false,
          width: "600px",
          customClass: {
            container: "swal-on-top",
          },
        });
        logWarn("Inappropriate content detected in problem detail", {
          detectedTerms: problemDetailCheck.detectedTerms,
        });
        formData.value.problemDetail = "";
        return;
      }

      isSubmitting.value = true;
      submitMessage.value = "";
      submitType.value = "";

      try {
        // Import Firebase functions
        const {
          db,
          collection,
          doc,
          setDoc,
          serverTimestamp,
          storage,
          storageRef,
          uploadBytes,
          getDownloadURL,
        } = await import("@/firebase");

        // Upload pictures to Firebase Storage if any
        const pictureUrls = [];
        if (uploadedPictures.value.length > 0) {
          for (const picture of uploadedPictures.value) {
            const timestamp = Date.now();
            const filename = `${timestamp}_${picture.name}`;
            const storageReference = storageRef(storage, `reports/${filename}`);

            try {
              await uploadBytes(storageReference, picture.file);
              const downloadUrl = await getDownloadURL(storageReference);
              pictureUrls.push({
                url: downloadUrl,
                filename: picture.name,
                uploadedAt: new Date().toISOString(),
              });
            } catch (uploadError) {
              console.error("Error uploading picture:", uploadError);
              // Continue with other uploads
            }
          }
        }

        // Create report data structure
        const reportData = {
          // Reporter information (fallback data for non-registered users)
          reporterName: formData.value.reporterName.trim(),
          reporterEmail: formData.value.reporterEmail.trim() || null,
          reporterType: formData.value.reporterType,
          reporterUid: currentUser.value?.uid || null, // Include UID if logged in

          // Reported user information (fallback data)
          reportedName: formData.value.reportedName.trim() || "Not specified",
          reportedEmail: formData.value.reportedEmail.trim() || null,
          reportedType: formData.value.reportedType,
          reportedUid: null, // No UID for guest reports

          // Report details
          category: formData.value.category,
          problemDetail: formData.value.problemDetail.trim(),

          // Status and timestamps
          status: "Pending",
          dateSent: serverTimestamp(),
          dateResolved: null,
          adminNotes: null,
          reviewedBy: null,
          attachments: [],
          pictures: pictureUrls,

          // Audit fields
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          archivedAt: null,
        };

        // Generate unique report ID
        const reportId = `guest_report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Save to Firebase
        await setDoc(doc(db, "reports", reportId), reportData);

        console.log("✅ Report submitted successfully:", reportId);

        // Close modal immediately
        closeModal();
        resetForm();

        // Show success toast
        successReportId.value = reportId.substring(reportId.lastIndexOf("_") + 1);
        successToastMessage.value = "Report submitted successfully!";
        showSuccessToast.value = true;

        // Auto-hide toast after 4 seconds
        setTimeout(() => {
          showSuccessToast.value = false;
        }, 4000);
      } catch (error) {
        console.error("❌ Error submitting report:", error);

        // Show error message
        submitType.value = "error";
        submitMessage.value = "Failed to submit report. Please try again or contact support.";
      } finally {
        isSubmitting.value = false;
      }
    };

    // REAL-TIME CONTENT SAFETY VALIDATION - Watch for inappropriate content as user types
    // Watch reporter name for inappropriate content
    watch(
      () => formData.value.reporterName,
      (newValue) => {
        if (newValue.trim().length > 0) {
          const safetyCheck = validateTextInputSafety(newValue);
          if (!safetyCheck.isSafe) {
            Swal.fire({
              icon: "error",
              title: "Inappropriate Content Detected",
              html: `
              <p class="mb-3">The unsafe content has been automatically removed.</p>
              <p class="mb-3">ElevatED is an educational platform designed for high school students. All content must be:</p>
              <ul class="text-left list-disc pl-6 mb-3">
                <li>Educational and age-appropriate</li>
                <li>Free from profanity, explicit language, and violence</li>
                <li>Suitable for a learning environment</li>
              </ul>
            `,
              confirmButtonColor: "#ef4444",
              confirmButtonText: "I Understand",
              allowOutsideClick: false,
              allowEscapeKey: false,
              width: "600px",
              customClass: {
                container: "swal-on-top",
              },
            });
            logWarn("Real-time inappropriate content detected in reporter name", {
              detectedTerms: safetyCheck.detectedTerms,
            });
            // Clear the field immediately
            formData.value.reporterName = "";
          }
        }
      }
    );

    // Watch reported name for inappropriate content
    watch(
      () => formData.value.reportedName,
      (newValue) => {
        if (newValue.trim().length > 0) {
          const safetyCheck = validateTextInputSafety(newValue);
          if (!safetyCheck.isSafe) {
            Swal.fire({
              icon: "error",
              title: "Inappropriate Content Detected",
              html: `
              <p class="mb-3">The unsafe content has been automatically removed.</p>
              <p class="mb-3">ElevatED is an educational platform designed for high school students. All content must be:</p>
              <ul class="text-left list-disc pl-6 mb-3">
                <li>Educational and age-appropriate</li>
                <li>Free from profanity, explicit language, and violence</li>
                <li>Suitable for a learning environment</li>
              </ul>
            `,
              confirmButtonColor: "#ef4444",
              confirmButtonText: "I Understand",
              allowOutsideClick: false,
              allowEscapeKey: false,
              width: "600px",
              customClass: {
                container: "swal-on-top",
              },
            });
            logWarn("Real-time inappropriate content detected in reported name", {
              detectedTerms: safetyCheck.detectedTerms,
            });
            // Clear the field immediately
            formData.value.reportedName = "";
          }
        }
      }
    );

    // Watch problem detail for inappropriate content
    watch(
      () => formData.value.problemDetail,
      (newValue) => {
        if (newValue.trim().length > 0) {
          const safetyCheck = validateTextInputSafety(newValue);
          if (!safetyCheck.isSafe) {
            Swal.fire({
              icon: "error",
              title: "Inappropriate Content Detected",
              html: `
              <p class="mb-3">The unsafe content has been automatically removed.</p>
              <p class="mb-3">ElevatED is an educational platform designed for high school students. All content must be:</p>
              <ul class="text-left list-disc pl-6 mb-3">
                <li>Educational and age-appropriate</li>
                <li>Free from profanity, explicit language, and violence</li>
                <li>Suitable for a learning environment</li>
              </ul>
            `,
              confirmButtonColor: "#ef4444",
              confirmButtonText: "I Understand",
              allowOutsideClick: false,
              allowEscapeKey: false,
              width: "600px",
              customClass: {
                container: "swal-on-top",
              },
            });
            logWarn("Real-time inappropriate content detected in problem detail", {
              detectedTerms: safetyCheck.detectedTerms,
            });
            // Clear the field immediately
            formData.value.problemDetail = "";
          }
        }
      }
    );

    // Sanitized submit message for safe HTML rendering
    const sanitizedSubmitMessage = computed(() => {
      return submitMessage.value ? sanitizeHtml(submitMessage.value) : "";
    });

    // Draggable button handlers
    const startDrag = (event) => {
      isDragging.value = true;
      hasDragged.value = false; // Reset drag flag

      // Get touch or mouse coordinates
      const clientX = event.type.includes("touch") ? event.touches[0].clientX : event.clientX;
      const clientY = event.type.includes("touch") ? event.touches[0].clientY : event.clientY;

      dragStart.value = {
        x: clientX - buttonPosition.value.x,
        y: clientY - buttonPosition.value.y,
      };

      // Prevent text selection during drag
      event.preventDefault();
    };

    const onDrag = (event) => {
      if (!isDragging.value) return;

      // Get touch or mouse coordinates
      const clientX = event.type.includes("touch") ? event.touches[0].clientX : event.clientX;
      const clientY = event.type.includes("touch") ? event.touches[0].clientY : event.clientY;

      let newX = clientX - dragStart.value.x;
      let newY = clientY - dragStart.value.y;

      // Get window dimensions and button size
      const padding = 20; // Keep button at least 20px from edges
      const buttonSize = 60; // Button width/height
      const maxX = window.innerWidth - buttonSize - padding;
      const maxY = window.innerHeight - buttonSize - padding;

      // Constrain position to stay within screen bounds
      newX = Math.max(-window.innerWidth + buttonSize + padding, Math.min(newX, maxX));
      newY = Math.max(-window.innerHeight + buttonSize + padding, Math.min(newY, maxY));

      // Check if user actually moved (more than 5px to avoid accidental micro-movements)
      const movedDistance = Math.sqrt(
        Math.pow(newX - buttonPosition.value.x, 2) + Math.pow(newY - buttonPosition.value.y, 2)
      );

      if (movedDistance > 5) {
        hasDragged.value = true;
      }

      buttonPosition.value = {
        x: newX,
        y: newY,
      };

      event.preventDefault();
    };

    const endDrag = () => {
      isDragging.value = false;
      // Don't reset hasDragged here - it will be checked in handleButtonClick
    };

    // Reset button position to default (double-click/tap)
    const resetPosition = () => {
      buttonPosition.value = { x: 0, y: 0 };
      localStorage.removeItem("fileReportButtonPosition");
    };

    // Handle button click - distinguish between drag and actual click
    const handleButtonClick = () => {
      // If user just dragged, don't open modal
      if (hasDragged.value) {
        hasDragged.value = false; // Reset for next interaction
        return;
      }

      // Check for double-click to reset position
      const currentTime = Date.now();
      if (currentTime - lastClickTime.value < 300) {
        // Double-click detected - reset position
        resetPosition();
        lastClickTime.value = 0;
        return;
      }
      lastClickTime.value = currentTime;

      // Open modal on single click (if didn't drag)
      openModal();
    };

    // Load saved button position from localStorage
    onMounted(() => {
      const savedPosition = localStorage.getItem("fileReportButtonPosition");
      if (savedPosition) {
        try {
          buttonPosition.value = JSON.parse(savedPosition);
        } catch (e) {
          console.error("Failed to load button position:", e);
        }
      }

      // Listen for open FileReport event
      console.log("🎧 FileReport: Event listener registered for EVENTS.OPEN_FILE_REPORT");
      eventBus.on(EVENTS.OPEN_FILE_REPORT, openModal);

      // ✅ TEST: Expose openModal to window for debugging
      if (typeof window !== "undefined") {
        // @ts-ignore
        window.testFileReportOpen = () => {
          console.log("🧪 Manual FileReport test triggered");
          openModal();
        };
        console.log("🛠️ Test function available: window.testFileReportOpen()");
      }
    });

    // Clean up event listener
    onUnmounted(() => {
      eventBus.off(EVENTS.OPEN_FILE_REPORT, openModal);
    });

    // Save button position when it changes
    watch(
      buttonPosition,
      (newPosition) => {
        if (!isDragging.value && (newPosition.x !== 0 || newPosition.y !== 0)) {
          localStorage.setItem("fileReportButtonPosition", JSON.stringify(newPosition));
        }
      },
      { deep: true }
    );

    return {
      isModalOpen,
      isSubmitting,
      submitMessage,
      sanitizedSubmitMessage,
      submitType,
      formData,
      isFormValid,
      categories,
      userTypes,
      reportedUserTypes,
      openModal,
      closeModal,
      resetForm,
      submitReport,
      currentUser,
      isLoggedIn,
      checkUserAuth,
      fieldTouched,
      markFieldAsTouched,
      problemDetailCharCount,
      problemDetailMaxLength,
      problemDetailMinLength,
      reportedNameMaxLength,
      reportedNameMinLength,
      reportedNameError,
      reporterEmailError,
      reportedEmailError,
      isValidName,
      isValidEmail,
      showSuccessToast,
      successToastMessage,
      successReportId,
      closeSuccessToast,
      // Draggable button
      buttonPosition,
      isDragging,
      buttonRef,
      startDrag,
      onDrag,
      endDrag,
      handleButtonClick,
      resetPosition,
      // Field refs for scrolling
      reporterEmailRef,
      reportedNameRef,
      reportedTypeRef,
      categoryRef,
      problemDetailRef,
      // Picture upload
      uploadedPictures,
      pictureInput,
      triggerPictureUpload,
      handlePictureUpload,
      removePicture,
      MAX_PICTURES,
    };
  },
};
</script>

<template>
  <!-- Floating Report Button -->
  <div
    ref="buttonRef"
    class="floating-report-container"
    :class="{ 'is-dragging': isDragging }"
    :style="{
      transform: `translate(${buttonPosition.x}px, ${buttonPosition.y}px)`,
      transition: isDragging ? 'none' : 'transform 0.2s ease',
    }"
    @mousedown="startDrag"
    @touchstart="startDrag"
    @mousemove="onDrag"
    @touchmove="onDrag"
    @mouseup="endDrag"
    @touchend="endDrag"
    @mouseleave="endDrag"
  >
    <button
      @click="handleButtonClick"
      class="floating-report-btn"
      :class="{ dragging: isDragging }"
      title="Report a Problem (Drag to move)"
    >
      <div class="icon-container">
        <svg class="report-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <span class="report-text">{{ isDragging ? "Move" : "Report a Problem" }}</span>
    </button>
  </div>

  <!-- Report Modal -->
  <div v-if="isModalOpen" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-inner">
        <!-- Modal Header -->
        <div class="modal-header">
          <h2 class="modal-title">File a Report</h2>
          <button @click="closeModal" class="close-btn">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Success/Error Messages -->
        <div
          v-if="submitMessage"
          :class="['message', submitType === 'success' ? 'message-success' : 'message-error']"
          v-html="sanitizedSubmitMessage"
        ></div>

        <!-- Modal Body -->
        <div class="modal-body">
          <form @submit.prevent class="report-form">
            <!-- Reporter Information -->
            <div class="form-section">
              <h3 class="section-title">Your Information</h3>
              <div class="form-grid">
                <div class="form-group">
                  <label for="reporterName" class="form-label"
                    >Name <span class="required">*</span></label
                  >
                  <input
                    id="reporterName"
                    v-model="formData.reporterName"
                    type="text"
                    class="form-input readonly-field"
                    :placeholder="
                      isLoggedIn ? 'Auto-filled from your profile' : 'Guest (cannot be changed)'
                    "
                    readonly
                    required
                  />
                  <div v-if="isLoggedIn" class="input-note">
                    <svg
                      class="w-4 h-4 inline mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Only admins will see your name. It will not be shared with others. See our
                    <a href="/privacy-policy" target="_blank" class="link">Privacy Policy</a> and
                    <a href="/terms-conditions" target="_blank" class="link">Terms & Conditions</a>.
                  </div>
                  <div v-else class="input-note">
                    <svg
                      class="w-4 h-4 inline mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Reports from guests are processed anonymously. Only admins will see this
                    information. See our
                    <a href="/privacy-policy" target="_blank" class="link">Privacy Policy</a> and
                    <a href="/terms-conditions" target="_blank" class="link">Terms & Conditions</a>.
                  </div>
                </div>
                <div class="form-group">
                  <label for="reporterEmail" class="form-label"
                    >Email <span class="required">*</span></label
                  >
                  <input
                    ref="reporterEmailRef"
                    id="reporterEmail"
                    v-model="formData.reporterEmail"
                    type="email"
                    class="form-input"
                    :class="{
                      'readonly-field': isLoggedIn,
                      'input-error': reporterEmailError,
                    }"
                    @input="markFieldAsTouched('reporterEmail')"
                    @blur="markFieldAsTouched('reporterEmail')"
                    :placeholder="
                      isLoggedIn ? 'Auto-filled from your profile' : 'Enter your email (required)'
                    "
                    :readonly="isLoggedIn"
                    required
                  />
                  <div v-if="reporterEmailError" class="error-message">
                    {{ reporterEmailError }}
                  </div>
                  <div class="input-note">
                    <svg
                      class="w-4 h-4 inline mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Your email will only be used to contact you about this report. Your email will
                    not be shared.
                  </div>
                </div>
              </div>
            </div>

            <!-- Reported User Information -->
            <div class="form-section">
              <h3 class="section-title">Who/What are you reporting?</h3>
              <div class="form-grid">
                <div class="form-group">
                  <label for="reportedName" class="form-label"
                    >Name/Subject <span class="required">*</span></label
                  >
                  <input
                    ref="reportedNameRef"
                    id="reportedName"
                    v-model="formData.reportedName"
                    type="text"
                    class="form-input"
                    :class="{
                      'input-error': reportedNameError,
                    }"
                    @input="markFieldAsTouched('reportedName')"
                    @blur="markFieldAsTouched('reportedName')"
                    :maxlength="reportedNameMaxLength"
                    placeholder="e.g., John Doe, System Login Issue, etc."
                    required
                  />
                  <div class="input-meta">
                    <div v-if="reportedNameError" class="error-message">
                      {{ reportedNameError }}
                    </div>
                    <div class="char-counter-small">
                      {{ formData.reportedName.length }}/{{ reportedNameMaxLength }}
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label for="reportedEmail" class="form-label">Email (if applicable)</label>
                  <input
                    id="reportedEmail"
                    v-model="formData.reportedEmail"
                    type="email"
                    class="form-input"
                    :class="{
                      'input-error': reportedEmailError,
                    }"
                    @input="markFieldAsTouched('reportedEmail')"
                    @blur="markFieldAsTouched('reportedEmail')"
                    placeholder="Enter email if known (optional)"
                  />
                  <div v-if="reportedEmailError" class="error-message">
                    {{ reportedEmailError }}
                  </div>
                  <div class="input-note">
                    <svg
                      class="w-4 h-4 inline mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Optional - if you are reporting someone, they will not be informed.
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label for="reportedType" class="form-label"
                  >Type <span class="required">*</span></label
                >
                <select
                  ref="reportedTypeRef"
                  id="reportedType"
                  v-model="formData.reportedType"
                  class="form-select"
                  :class="{ 'input-error': fieldTouched.reportedType && !formData.reportedType }"
                  @change="markFieldAsTouched('reportedType')"
                  @blur="markFieldAsTouched('reportedType')"
                  required
                >
                  <option value="">Select what you are reporting</option>
                  <option v-for="type in reportedUserTypes" :key="type.value" :value="type.value">
                    {{ type.label }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Report Details -->
            <div class="form-section">
              <h3 class="section-title">Report Details</h3>
              <div class="form-group">
                <label for="category" class="form-label"
                  >Category <span class="required">*</span></label
                >
                <select
                  ref="categoryRef"
                  id="category"
                  v-model="formData.category"
                  class="form-select"
                  :class="{ 'input-error': fieldTouched.category && !formData.category }"
                  @change="markFieldAsTouched('category')"
                  @blur="markFieldAsTouched('category')"
                  required
                >
                  <option value="">Select a category</option>
                  <option v-for="category in categories" :key="category" :value="category">
                    {{ category }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label for="problemDetail" class="form-label"
                  >Problem Description <span class="required">*</span></label
                >
                <textarea
                  ref="problemDetailRef"
                  id="problemDetail"
                  v-model="formData.problemDetail"
                  class="form-textarea"
                  :class="{
                    'input-error':
                      fieldTouched.problemDetail &&
                      (formData.problemDetail.length < problemDetailMinLength ||
                        formData.problemDetail.length > problemDetailMaxLength ||
                        !formData.problemDetail.trim()),
                    'char-limit-warning': problemDetailCharCount > problemDetailMaxLength * 0.9,
                  }"
                  @input="markFieldAsTouched('problemDetail')"
                  @blur="markFieldAsTouched('problemDetail')"
                  :maxlength="problemDetailMaxLength"
                  rows="4"
                  placeholder="Please provide a detailed description of the issue..."
                  required
                ></textarea>
                <div
                  class="char-counter"
                  :class="{
                    'char-limit-warning': problemDetailCharCount > problemDetailMaxLength * 0.9,
                    'char-limit-error': problemDetailCharCount > problemDetailMaxLength,
                  }"
                >
                  {{ problemDetailCharCount }}/{{ problemDetailMaxLength }} characters
                  <span
                    v-if="
                      formData.problemDetail.length > 0 &&
                      problemDetailCharCount < problemDetailMinLength
                    "
                    class="min-char-warning"
                  >
                    (minimum {{ problemDetailMinLength }} required)
                  </span>
                </div>
              </div>

              <!-- Picture Upload (Optional) -->
              <div class="form-group">
                <label class="form-label">
                  Pictures (Optional)
                  <span class="text-xs text-gray-500 font-normal ml-2"
                    >JPEG/PNG only, max 2MB each, up to 3 pictures</span
                  >
                </label>
                <input
                  ref="pictureInput"
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  @change="handlePictureUpload"
                  class="hidden"
                />
                <button
                  type="button"
                  @click="triggerPictureUpload"
                  :disabled="uploadedPictures.length >= MAX_PICTURES"
                  class="upload-btn"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {{ uploadedPictures.length > 0 ? "Add Another Picture" : "Upload Pictures" }}
                  ({{ uploadedPictures.length }}/{{ MAX_PICTURES }})
                </button>

                <!-- Picture Previews -->
                <div v-if="uploadedPictures.length > 0" class="picture-previews">
                  <div
                    v-for="(picture, index) in uploadedPictures"
                    :key="index"
                    class="picture-preview"
                  >
                    <img :src="picture.preview" :alt="picture.name" class="preview-image" />
                    <div class="preview-overlay">
                      <span class="preview-name">{{ picture.name }}</span>
                      <button
                        type="button"
                        @click="removePicture(index)"
                        class="remove-picture-btn"
                        title="Remove picture"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <!-- Modal Footer -->
        <!-- Modal Footer -->
        <div class="modal-footer">
          <button @click="closeModal" class="btn btn-cancel">Cancel</button>
          <button @click="submitReport" :disabled="isSubmitting" class="btn btn-submit">
            {{ isSubmitting ? "Submitting..." : "Submit Report" }}
          </button>
        </div>
      </div>
      <!-- Close modal-inner -->
    </div>
    <!-- Close modal-container -->
  </div>
  <!-- Close modal-overlay -->

  <!-- Success Toast Notification -->
  <div v-if="showSuccessToast" class="success-toast" @click="closeSuccessToast">
    <div class="toast-content">
      <div class="toast-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
          />
        </svg>
      </div>
      <div class="toast-text">
        <div class="toast-title">{{ successToastMessage }}</div>
        <div class="toast-subtitle">
          Report ID: <strong>{{ successReportId }}</strong>
        </div>
        <div class="toast-info">We'll review this within 24-48 hours</div>
      </div>
      <button @click="closeSuccessToast" class="toast-close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Floating Report Button */
.floating-report-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  cursor: move;
  cursor: grab;
  user-select: none;
  touch-action: none; /* Prevent default touch behaviors */
}

.floating-report-container.is-dragging {
  cursor: grabbing;
  z-index: 1001; /* Higher z-index while dragging */
}

.floating-report-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #8b0000; /* Dark red background color */
  color: #ffffff; /* White color for icon and text */
  box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.1);
  cursor: inherit; /* Inherit cursor from container */
  transition: all 0.6s ease;
  animation: pulse 2s infinite;
  overflow: hidden;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  position: relative; /* For absolute positioning of icon container */
  pointer-events: auto; /* Ensure button is clickable */
  /* Default circular state */
  width: 60px;
  height: 60px;
  border-radius: 30px;
}

.floating-report-btn.dragging {
  animation: none; /* Disable pulse animation while dragging */
  opacity: 0.9;
  box-shadow: 6px 6px 0px rgba(0, 0, 0, 0.15);
}

.floating-report-btn:hover {
  background: #b22222; /* Slightly lighter red on hover */
  color: #ffffff; /* Keep white text on hover */
  box-shadow: 6px 6px 0px rgba(0, 0, 0, 0.15);
  animation: none;
  transform: translateY(-2px); /* Slight lift on hover */

  /* Expanded state on hover */
  width: auto;
  min-width: 200px;
  padding: 0 24px;
  border-radius: 30px;
}

/* Don't expand when dragging */
.floating-report-btn.dragging:hover {
  width: 60px;
  min-width: unset;
  padding: 0;
  border-radius: 30px;
}

.floating-report-btn:active {
  transform: scale(0.95) translateY(-1px);
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
}

.drag-indicator {
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 10;
  color: white;
}

.floating-report-container:hover .drag-indicator {
  opacity: 1;
}

.floating-report-btn.dragging .drag-indicator {
  opacity: 0.8;
}

.report-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #ffffff; /* White icon */
  position: relative;
}

.report-text {
  font-size: 14px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  margin-left: 24px; /* Increased margin to account for icon width */
  white-space: nowrap;
  opacity: 0;
  max-width: 0;
  transition: all 0.6s ease;
  overflow: hidden;
  color: #ffffff; /* White text */
  position: relative;
  z-index: 2;
}

.floating-report-btn:hover .report-text {
  opacity: 1;
  max-width: 180px;
}

.floating-report-btn:hover .icon-container {
  width: auto;
  position: relative;
  justify-content: flex-start;
  margin-left: 12px;
}

/* Improved pulse animation */
@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.1);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 5px 5px 0px rgba(0, 0, 0, 0.12);
    background: #a00000; /* Slightly darker red during pulse */
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(2px);
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease-out;
}

/* Inner scrollable area */
.modal-inner {
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 13px; /* Slightly smaller to account for border */
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Modal Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: between;
  padding: 24px 32px 16px 32px;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  flex: 1;
  font-family: "Inter", sans-serif;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

/* Messages */
.message {
  margin: 16px 32px;
  padding: 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  animation: messageSlideIn 0.3s ease-out;
}

@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-success {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #065f46;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

.message-error {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #991b1b;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
}

.success-message-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.success-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: successPulse 0.6s ease-out;
}

@keyframes successPulse {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-icon svg {
  width: 20px;
  height: 20px;
}

.success-text h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #065f46;
}

.success-text p {
  margin: 0 0 8px 0;
  line-height: 1.5;
  color: #047857;
}

.success-text p:last-child {
  margin-bottom: 0;
}

.success-text strong {
  font-weight: 700;
  color: #065f46;
  background: rgba(16, 185, 129, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

/* Error message styles */
.error-message {
  font-size: 12px;
  color: #dc2626;
  margin-top: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-message::before {
  content: "⚠";
  font-size: 10px;
}

/* Input meta container for error and counter */
.input-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}

.char-counter-small {
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
}

/* Modal Body */
.modal-body {
  padding: 24px 32px;
}

.report-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
  font-family: "Inter", sans-serif;
  padding-bottom: 8px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 640px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  font-family: "Inter", sans-serif;
}

.form-input,
.form-select,
.form-textarea {
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: "Inter", sans-serif;
  transition: border-color 0.2s ease;
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: #9ca3af;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: "Inter", sans-serif;
  line-height: 1.5;
}

/* Form Validation Styles - Red borders only after interaction */
.required {
  color: #dc2626;
  font-weight: 700;
}

/* Explicit error class for touched invalid fields */
.input-error {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1) !important;
}

/* Focus states - blue borders */
.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Readonly field styling */
.readonly-field {
  background-color: #f5f5f5 !important;
  cursor: not-allowed;
}

/* Character counter styles */
.char-counter {
  font-size: 12px;
  color: #6b7280;
  text-align: right;
  margin-top: 4px;
}

.char-limit-warning {
  color: #dc2626 !important;
  border-color: #dc2626 !important;
}

.char-limit-error {
  color: #dc2626 !important;
  border-color: #dc2626 !important;
}

.min-char-warning {
  color: #dc2626;
  font-size: 11px;
  margin-left: 8px;
}

.input-note {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
}

.input-note svg {
  display: inline;
  vertical-align: top;
  margin-top: 1px;
}

.link {
  color: #3b82f6;
  text-decoration: underline;
}

.link:hover {
  color: #1d4ed8;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 32px 24px 32px;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-family: "Inter", sans-serif;
  min-width: 120px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background: #f9fafb;
  color: #374151;
}

.btn-cancel:hover:not(:disabled) {
  background: #f3f4f6;
}

.btn-submit {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.25);
}

.btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #1d4ed8, #1e3a8a);
  transform: translateY(-1px);
  box-shadow: 0 6px 8px rgba(59, 130, 246, 0.35);
}

/* Responsive Design */
@media (max-width: 768px) {
  .floating-report-container {
    right: 15px;
    bottom: 25px; /* Move slightly up for better visibility */
  }

  .floating-report-btn {
    width: 58px;
    height: 58px;
    box-shadow: 3px 3px 0px rgba(0, 0, 0, 0.1);
  }

  .floating-report-btn:hover {
    box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.12);
    min-width: 180px;
    padding: 0 20px;
  }

  .report-icon {
    width: 24px;
    height: 24px;
    margin: 0 auto; /* Keep icon centered in responsive view */
  }

  .report-text {
    font-size: 10px;
  }

  .modal-container {
    margin: 10px;
    max-height: 95vh;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 20px;
    padding-right: 20px;
  }

  .modal-footer {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .floating-report-container {
    right: 20px;
    bottom: 30px;
    z-index: 1000; /* Ensure button appears above other elements */
  }

  .floating-report-btn {
    width: 68px; /* Increased size for mobile */
    height: 68px; /* Increased size for mobile */
    box-shadow: 4px 4px 0px rgba(0, 0, 0, 0.1);
    animation: mobilePulse 2s infinite; /* Enhanced animation for mobile */
  }

  .floating-report-btn:hover {
    box-shadow: 5px 5px 0px rgba(0, 0, 0, 0.12);
    min-width: 180px;
    padding: 0 20px;
  }

  .report-icon {
    width: 28px; /* Larger icon for mobile */
    height: 28px; /* Larger icon for mobile */
    margin: 0 auto; /* Keep icon centered in mobile view */
  }

  .report-text {
    font-size: 12px; /* Slightly larger text */
  }

  /* Enhanced pulse animation for mobile */
  @keyframes mobilePulse {
    0% {
      transform: scale(1);
      box-shadow: 4px 4px 0px #000;
    }
    50% {
      transform: scale(1.05) translateY(-2px);
      box-shadow: 6px 6px 0px #000;
      background: #a00000;
    }
    100% {
      transform: scale(1);
      box-shadow: 4px 4px 0px #000;
    }
  }
}

/* Success Toast Notification */
.success-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999; /* Higher than modal */
  animation: toastSlideIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
}

@keyframes toastSlideIn {
  from {
    opacity: 0;
    transform: translateX(100%) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

.toast-content {
  background: white;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 320px;
  max-width: 400px;
  font-family: "Inter", sans-serif;
  animation: toastPulse 0.6s ease-out;
}

@keyframes toastPulse {
  0% {
    transform: scale(0.95);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

.toast-icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: iconBounce 0.8s ease-out;
}

@keyframes iconBounce {
  0%,
  20%,
  60%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  80% {
    transform: translateY(-4px);
  }
}

.toast-icon svg {
  width: 16px;
  height: 16px;
}

.toast-text {
  flex: 1;
  color: #000;
}

.toast-title {
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin-bottom: 4px;
}

.toast-subtitle {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 2px;
}

.toast-subtitle strong {
  color: #10b981;
  font-weight: 700;
  background: rgba(16, 185, 129, 0.1);
  padding: 1px 4px;
  border-radius: 3px;
}

.toast-info {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

.toast-close {
  flex-shrink: 0;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  background: #f3f4f6;
  color: #374151;
  transform: scale(1.1);
}

.toast-close svg {
  width: 14px;
  height: 14px;
}

/* Success toast hover effect */
.success-toast:hover .toast-content {
  transform: translateY(-2px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

/* Extra small screen optimization */
@media (max-width: 360px) {
  .floating-report-container {
    right: 15px;
    bottom: 25px;
  }

  .floating-report-btn {
    width: 72px; /* Even larger for very small screens */
    height: 72px;
    box-shadow: 5px 5px 0px rgba(0, 0, 0, 0.1);
  }

  .report-icon {
    width: 30px; /* Even larger icon for very small screens */
    height: 30px;
  }
} /* Mobile responsive for toast */
@media (max-width: 768px) {
  .success-toast {
    top: 15px;
    right: 15px;
    left: 15px;
  }

  .toast-content {
    min-width: auto;
    max-width: none;
    padding: 14px 16px;
  }

  .toast-title {
    font-size: 15px;
  }

  .toast-subtitle {
    font-size: 12px;
  }

  .toast-info {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .success-toast {
    top: 10px;
    right: 10px;
    left: 10px;
  }

  .toast-content {
    padding: 12px 14px;
    gap: 10px;
  }

  .toast-icon {
    width: 24px;
    height: 24px;
  }

  .toast-icon svg {
    width: 14px;
    height: 14px;
  }
}

/* Picture Upload Styles */
.upload-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "Inter", sans-serif;
}

.upload-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #1d4ed8, #1e3a8a);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.upload-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.picture-previews {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.picture-preview {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.picture-preview:hover .preview-overlay {
  opacity: 1;
}

.preview-name {
  font-size: 11px;
  color: white;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

.remove-picture-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-picture-btn:hover {
  background: #dc2626;
  transform: scale(1.1);
}

/* SweetAlert customization to ensure it appears on top of everything */
:deep(.swal-on-top) {
  z-index: 10000 !important;
}

:global(.swal2-container) {
  z-index: 10000 !important;
}
</style>
